import { Block } from "./components/Block";
import { ILayerOptions, IAnimateProps, OverflowOptions, ISize, EditorOptions, ServiceOptions, IPoint } from "./types";
import { ParamValue } from "./components/Params";
import { IGenerator } from "./generators/Generator";
import { Select } from "./editors/Select";
/**
 * Props for Layout.
 *
 * Note that you can add a separate style property that will be merged
 * internally for the root element.
 * @noInheritDoc
 */
export interface ILayoutProps extends React.HTMLProps<HTMLElement> {
  /**
   * Unique name is required.
   */
  name: string
  /**
   * The default is ServiceOptions.none.
   */
  service?: ServiceOptions
  /**
   * The default is EditorOptions.none.
   */
  editorOptions?: EditorOptions
  /**
   * The generator is required and should be unique for each Layout. Reusing
   * generators is possible but not recommended.
   *
   * Generally you will want to use a generator function to define its value,
   * but you can also define init and create methods in a class and then use
   * those to create an instance of the generator.
   *
   * ```ts
   * class MyClass
   *
   *   constructor() {
   *     ...
   *     this._g = new Generator(
   *        'name',
   *        this.init.bind(this),
   *        this._params,
   *        this.create.bind(this)
   *      );
   *   }
   *
   *   private init = (g: IGenerator): Blocks => {
   *     ...
   *   }
   *
   *   private create = (args: ICreate): Block | undefined => {
   *     ...
   *   }
   * }
   * ```
   */
  g: IGenerator
  /**
   * Defines the size of the container for this Layout. The default size of the
   * Layout is computed internally, but if this property is set then it will be
   * used for the size of the Layout.
   *
   * One use of this property is to specify the container size for nested Layouts.
   * It is also useful for tests.
   *
   * ```ts
   *  <Layout
   *    ... >
   *    <Panel
   *      ... >
   *      {(args: IMetaDataArgs) => (
   *        <Layout
   *          ...
   *          containersize={{width: args.container.width, height: args.container.height}}
   *        >
   * ```
   *
   * Note: The computation of the container size internally requires one render
   * of the Layout where the containersize will be {width: 0, height: 0} so setting
   * this property results in a slight performance gain.
   */
  containersize?: ISize
  /**
   * Sets initial params for this Layout.
   *
   * The values are an array of [string, ParamValue]. The following example
   * appends a new value to an existing array defined in params.json:
   *
   * ```ts
   *  import * as data from '../assets/data/params.json'
   *
   *  ...
   *
   *  <Layout
   *    ...
   *    params={[
   *      ...data['rlg.intro'] as Array<[string, ParamValue]>,
   *      ['velocity', 0.05]
   *    ]}
   *  >
   * ```
   */
  params?: Array<[string, ParamValue]>
  /**
   * This is the same as the css overflow settings.
   *
   * We do not recommend that you override this setting with
   * the style property since this setting is applied dynamically.
   */
  overflowX?: OverflowOptions
  /**
   * This is the same as the css overflow settings.
   *
   * We do not recommend that you override this setting with
   * the style property since this setting is applied dynamically.
   */
  overflowY?: OverflowOptions
  /**
   * Controls the animation behavior of the Layout.
   */
  animate?: IAnimateProps

  layers?: ILayerOptions

  onUpdate?: () => void
}

/**
 * internal use only
 * @ignore
 */
export interface ILayoutState {
  boundingLeftTop: IPoint
  width: number
  height: number
  update: number
  contextMenu: Block | undefined
  contextMenuActive: boolean
  devicePixelRatio: number
}

export interface ILayoutInstance {
  getBoundingLeftTop: () => IPoint
  onUpdate: () => void
  edit: () => boolean
  onResize: (width: number, height: number) => void
  selectCallback: (instance: Select) => void
  select: Select | undefined
}

/**
 * internal use only
 * @ignore
 */
export const gLayouts: Map<string, boolean> = new Map()

/**
 * internal use only
 * @ignore
 */
export const gInstance: {
  inProgress: number
} = {inProgress: 0}

/**
 * internal use only
 * @ignore
 */
export const gContext: Map<string, any> = new Map()